import styled from "styled-components";
import { toast } from "react-toastify";
import { useSelector, useDispatch } from "react-redux";
import { useContext, useEffect, useState } from "react";

import useAudioPlayer from "../../../hooks/useAudioPlayer";
import { client } from "../../../redux/store";
import { VideoEditorContext } from "../../../App";
import { SentryErrors } from "../../../lib/sentry";
import { checkIfZoneCached } from "../../../lib/editorUtils";
import { HumansParagraph } from "../../../types/humansProject";
import { getEmptyParagraphs } from "../../../hooks/useAIHumans";
import { getProjectAudio } from "../../../redux/reducers/projectReducer";
import { Popups, updatePopup } from "../../../redux/actions/popupsActions";
import { GENERATE_VOICE_SERVER } from "../../../redux/actions/actorActions";
import { CircularProgress, PlayIcon, PauseIcon } from "../../../components/Icons/Icons";

interface PlaylistItem {
  sceneId: number;
  audioPath: string | undefined;
  text: string;
}

interface Props {
  paragraphs: HumansParagraph[];
}

const AIHumansPreviewPlayer = ({ paragraphs }: Props) => {
  const dispatch = useDispatch();
  const [isAudioLoading, setIsAudioLoading] = useState(false);
  const [isPreviewAllowed, setIsPreviewAllowed] = useState(false);

  const [playlist, setPlaylist] = useState<PlaylistItem[] | null>();
  const [playlistMemorized, setPlaylistMemorized] = useState<PlaylistItem[] | null>();
  const [playlistIndex, setPlaylistIndex] = useState(0);
  const [isPlaylistChanged, setIsPlaylistChanged] = useState(true);

  const { cachedZonesAudio } = useSelector(getProjectAudio);
  const { scenes, activeSceneId, handleChangeActiveScene } = useContext(VideoEditorContext);

  const {
    audioPlayer,
    currentTime,
    duration,
    playing,
    onPlaying,
    isFetching,
    setPlaying,
    onLoadStart,
    onLoadedMetadata,
  } = useAudioPlayer();

  const checkPlaylistPropsChanged = () => {
    let newPlaylist: PlaylistItem[] = [];
    let isAllDataCorrect = true;

    for (let element = 0; element < scenes.length; element++) {
      let sceneId = scenes[element].id;
      let text = JSON.stringify(paragraphs[element].data);
      let audioPath = scenes[element]?.audioPath;

      newPlaylist.push({ sceneId, audioPath, text });

      if (!paragraphs[element].data[0].text && !audioPath) {
        isAllDataCorrect = false;
      }
    }

    if (JSON.stringify(newPlaylist) !== JSON.stringify(playlistMemorized)) {
      setIsPlaylistChanged(true);
      setPlaylistMemorized(newPlaylist);
    }
    setIsPreviewAllowed(isAllDataCorrect);
  };

  const onEveryAudioEnd = (audioLinks: PlaylistItem[], currentIndex: number) => {
    if (!audioPlayer.current) return;

    const playNextAudio = () => {
      const nextIndex = currentIndex + 1;

      if (nextIndex < audioLinks.length && audioLinks[nextIndex]) {
        audioPlayer.current.src = audioLinks[nextIndex].audioPath;
        audioPlayer.current.play();

        handleChangeActiveScene(audioLinks[nextIndex].sceneId);

        audioPlayer.current.onended = () => onEveryAudioEnd(audioLinks, nextIndex);
      } else {
        setPlaying(false);
        console.log("All audio has been played.");
      }
    };

    playNextAudio();
  };

  const prepareAudioPlayer = async () => {
    let newPlaylist: PlaylistItem[] = [];
    let audioIndex: number = playlistIndex;

    if (isPlaylistChanged) {
      audioIndex = 0;
      if (isAudioLoading) return;

      setIsAudioLoading(true);
      try {
        const emptyIndexes = getEmptyParagraphs(paragraphs)
          .filter((element) => element !== undefined)
          .filter((element) => {
            if (element !== undefined) {
              if (scenes[element]?.audioPath) return false;
              else return true;
            } else {
              return false;
            }
          })
          .map((value) => (value !== undefined ? value + 1 : value));

        if (emptyIndexes.length > 0) {
          toast.error(`Please, add scripts (on slides ${emptyIndexes.join(", ")}) before preview`);
          return;
        }

        for (let element = 0; element < paragraphs.length; element++) {
          const scene = scenes[element];
          const paragraph = paragraphs[element];

          if (scene?.audioPath) {
            newPlaylist.push({
              sceneId: scene.id,
              audioPath: scene.audioPath,
              text: JSON.stringify(paragraph.data),
            });
            continue;
          }

          for (let index = 0; index < paragraph.data.length; index++) {
            const data = { ...paragraph.data[index], actorId: paragraph.actorId, paragraphIndex: index };

            const cachedAudioPath = checkIfZoneCached(data, cachedZonesAudio);
            if (cachedAudioPath?.outputUrl) {
              newPlaylist.push({
                sceneId: scene.id,
                audioPath: cachedAudioPath.outputUrl,
                text: JSON.stringify(paragraph.data[index]),
              });
              continue;
            }
            try {
              const response = await client.post("/actor/generateVoice", {
                data: [data],
                isAIHuman: true,
              });

              const audioPath = response.data.data[0].outputUrl;
              if (audioPath) {
                newPlaylist.push({
                  sceneId: scene.id,
                  audioPath: audioPath,
                  text: JSON.stringify(paragraph.data[index]),
                });

                dispatch({
                  type: `${GENERATE_VOICE_SERVER}_SUCCESS`,
                  payload: { data: response.data },
                  meta: {
                    previousAction: {
                      payload: {
                        request: {
                          data: {
                            data: [data],
                            isAIHuman: true,
                          },
                        },
                      },
                    },
                  },
                });
              }
            } catch (error: any) {
              if (error?.response?.data?.message === "Security notice: Trial limit has reached") {
                dispatch(
                  updatePopup({
                    popup: Popups.notEnoughCreditsPopup,
                    status: true,
                    prefilled: {
                      title: "You have reached the Free user limit.",
                      description: "Upgrade now to continue",
                    },
                  }),
                );
              } else {
                toast.error(error?.response?.data?.message || SentryErrors.SERVER_ERROR_WHILE_GENERATING_VOICE.toast);
              }
              return;
            }
          }
        }
        setPlaylist(newPlaylist);
        setIsPlaylistChanged(false);
      } catch (err) {
        console.log("Error: ", err);
      } finally {
        setIsAudioLoading(false);
      }
    } else {
      newPlaylist = playlist as PlaylistItem[];
    }

    audioPlayer.current.src = newPlaylist[audioIndex].audioPath;
    audioPlayer.current.onended = () => onEveryAudioEnd(newPlaylist as PlaylistItem[], audioIndex);
  };

  const handlePlayPause = async () => {
    if (!isPreviewAllowed) return;

    if (isPlaylistChanged) {
      setPlaylistIndex(0);
      handleChangeActiveScene(scenes[0].id);
    }
    await prepareAudioPlayer();

    if (playing) {
      audioPlayer.current.pause();
      setPlaying(false);
    } else {
      audioPlayer.current.play();
      setPlaying(true);
    }
  };

  const handleMissedAudioAlert = () => {
    if (!playlist) return;
    const missingIndexes = (playlist as PlaylistItem[])
      .map((item, index) => (item.audioPath === "" && item.text === "" ? index + 1 : null))
      .filter((index) => index !== null);
    toast.error(`Some scripts are missing. Please check slides ${missingIndexes.join(", ")} and try again.`);
  };

  useEffect(() => {
    checkPlaylistPropsChanged();
  }, [scenes, paragraphs]);

  useEffect(() => {
    setPlaylistIndex(playlist?.findIndex((item) => item.sceneId === activeSceneId) || 0);
  }, [activeSceneId]);

  return (
    <Bottom>
      {(!isPreviewAllowed || isAudioLoading) && <DisabledLayer onClick={handleMissedAudioAlert} />}
      <TimeLineWrapper onClick={handlePlayPause}>
        <audio
          ref={audioPlayer}
          onTimeUpdate={onPlaying}
          onLoadedMetadata={onLoadedMetadata}
          onLoadStart={onLoadStart}
        />
        <IconWrapper>
          {isFetching || isAudioLoading ? (
            <CircularProgress color="#F0F0F3" />
          ) : !playing || currentTime === duration ? (
            <PlayIcon />
          ) : (
            <PauseIcon />
          )}
        </IconWrapper>
        <EstimatedTime>Preview</EstimatedTime>
      </TimeLineWrapper>
    </Bottom>
  );
};

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    display: block;
    width: 16px;
    height: 16px;
    transform: translateX(1px);
  }
`;

const Bottom = styled.div`
  position: relative;
  /* position: sticky;
  bottom: 0;
  cursor: pointer;
  left: 0;
  border-radius: 10px 10px 0 0;
  background: ${({ theme }) => theme.primaryBackground};
  z-index: 10;
  width: 100%; */
  /* padding: 16px 0 98px; */
`;

const DisabledLayer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 10;
`;

const TimeLineWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  height: 34px;
  width: 100px;
  border-radius: 5px;
  border: 1px solid #3100f7;
  gap: 8px;
  font-size: 14px;
  cursor: pointer;

  button {
    width: 24px;
    height: 24px;
    background: transparent;

    svg {
      width: 14px;
      height: 14px;
    }
  }
`;

const EstimatedTime = styled.span`
  font-family: "Mulish";
  font-style: normal;
  font-weight: 600;
  font-size: 14px;
  line-height: 20px;
  color: ${({ theme }) => theme.primaryText};
  /* opacity: 0.4; */
`;

export default AIHumansPreviewPlayer;
